import { useRef, type MouseEvent as ReactMouseEvent } from 'react';

import { cx } from '@/components/ui/classNames';

import { HeaderTooltip } from './HeaderTooltip';
import type { GridColumn, GridSort } from './types';

/** Mindestbreite beim Ziehen des Griffs (`AllowResizing`). */
const MIN_COLUMN_WIDTH = 30;

/**
 * Sortierrichtung als kleines Zeichen hinter der Beschriftung (REQ-21 §4). Der Entwurf setzt
 * dort keine Symbolschrift, sondern ein Zeichen in `--accent-2`; `aria-sort` an der Kopfzelle
 * trägt die Information für Hilfsmittel, das Zeichen ist rein optisch.
 */
const SORT_MARK = { ascending: '▲', descending: '▼' } as const;

export interface GridHeaderRowProps<T> {
  /** Sichtbare Spalten in Anzeigereihenfolge. */
  columns: GridColumn<T>[];
  sorts: GridSort[];
  /** `additive` entspricht der gehaltenen Strg-Taste (Mehrfachsortierung). */
  onToggleSort: (field: string, additive: boolean) => void;
  onResize: (field: string, width: number) => void;
  onReorder: (field: string, targetField: string) => void;
}

export function GridHeaderRow<T>(props: GridHeaderRowProps<T>) {
  const { columns, onReorder, onResize } = props;
  const dragged = useRef<string | null>(null);

  const startResize = (event: ReactMouseEvent<HTMLDivElement>, column: GridColumn<T>) => {
    // Verhindert zugleich, dass die Kopfzelle zu ziehen beginnt.
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = column.width;

    const move = (moveEvent: MouseEvent) => {
      onResize(column.field, Math.max(MIN_COLUMN_WIDTH, startWidth + moveEvent.clientX - startX));
    };
    const stop = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop);
  };

  return (
    <>
      {columns.map((column) => {
        const sort = props.sorts.find((entry) => entry.field === column.field);

        return (
          <th
            key={column.field}
            draggable
            aria-sort={sort?.direction ?? 'none'}
            className={cx('e-headercell', 'e-mousepointer', sort && `e-${sort.direction}`)}
            onClick={(event) => {
              props.onToggleSort(column.field, event.ctrlKey);
            }}
            onDragStart={() => {
              dragged.current = column.field;
            }}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDrop={(event) => {
              event.preventDefault();
              const source = dragged.current;
              dragged.current = null;
              if (source !== null && source !== column.field) onReorder(source, column.field);
            }}
          >
            <div className="e-headercelldiv">
              <HeaderTooltip label={column.headerText} />
              {sort === undefined ? null : (
                <span className="iwm-sort-mark" aria-hidden="true">
                  {SORT_MARK[sort.direction]}
                </span>
              )}
            </div>
            <div
              role="separator"
              aria-label="Spaltenbreite ändern"
              className="e-rhandler"
              onMouseDown={(event) => {
                startResize(event, column);
              }}
              onClick={(event) => {
                event.stopPropagation();
              }}
            />
          </th>
        );
      })}
    </>
  );
}

export default GridHeaderRow;
